import { reactive } from 'vue'
import type { ToolClock, ToolDateStyle } from './toolSettings'

// How the tools write dates and times (Settings > Tools). Kept here rather
// than read from the settings store so the label helpers stay testable; the
// app copies the writer's choice in at start (see services/toolColors). It is
// reactive, so labels computed from it follow a change straight away.

export const toolFormat = reactive<{ clock: ToolClock; dateStyle: ToolDateStyle }>({
  clock: 'auto',
  dateStyle: 'auto',
})

const LOCALES: Record<ToolDateStyle, string | undefined> = {
  auto: undefined,
  monthFirst: 'en-US',
  dayFirst: 'en-GB',
  iso: undefined,
}

/** true or false for a 12- or 24-hour clock; undefined follows the system. */
export function hour12(): boolean | undefined {
  return toolFormat.clock === 'auto' ? undefined : toolFormat.clock === '12'
}

const pad = (n: number) => String(n).padStart(2, '0')

/** A date in the writer's style. ISO keeps any weekday asked for, then YYYY-MM-DD. */
export function formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  if (toolFormat.dateStyle === 'iso' && options.day) {
    const iso = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    return options.weekday ? `${date.toLocaleDateString(undefined, { weekday: options.weekday })} ${iso}` : iso
  }
  return date.toLocaleDateString(LOCALES[toolFormat.dateStyle], options)
}

/** A time of day on the writer's clock, like 9:30 a.m. or 09:30. */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString(LOCALES[toolFormat.dateStyle], { hour: 'numeric', minute: '2-digit', hour12: hour12() })
}

/** A date with a time, as in a sprint's history row. */
export function formatDateTime(date: Date, options: Intl.DateTimeFormatOptions): string {
  return `${formatDate(date, options)}, ${formatTime(date)}`
}
